import { JitsiConference, JitsiMeet, JitsiUtil } from "lib/lib-jitsi-meet";
import { action, flow, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";

import $ from "jquery";
if (typeof window !== "undefined") {
    (window as any).$ = $;
}

import { Configuration, getDefaultJitsiManagerState } from "../../common";
import { WelcomeDialog } from "../ui";

export interface JitsiManagerChildProps {
    jitsi: JitsiMeet;
    roomName: string;
    room: JitsiConference;
    isHost: boolean;
    isReconnecting: boolean;
    onLeaveRoom: () => void;
    reconnect: () => void;
}

export interface JitsiManagerProps {
    roomName: string;
    useHighQualityAudio?: boolean;
    isHost: boolean;
    children: (props: JitsiManagerChildProps) => JSX.Element;
    showWelcomeDialog?: boolean;
}

@observer
export default class JitsiManager extends React.Component<JitsiManagerProps, never> {
    public readonly displayName = "JitsiManager";
    public static defaultProps: Partial<JitsiManagerProps> = {
        showWelcomeDialog: true,
    };

    @observable public manager = getDefaultJitsiManagerState();
    // module will be loaded async
    @observable public jitsi: JitsiMeet | undefined;
    @observable public jitsiUtil: JitsiUtil | undefined;

    public constructor(props: JitsiManagerProps, context: any) {
        super(props, context);
        if (typeof window !== "undefined") {
            this.loadAsyncModules();
        }
    }

    private loadAsyncModules = flow(function* (this: JitsiManager) {
        const jitisImport = yield import("lib/lib-jitsi-meet");
        this.jitsi = jitisImport.default;
        this.jitsiUtil = jitisImport.util;
        this.manager.isJitsiModuleLoaded = true;
        this.initializeJitsi();
    });

    @action
    private initializeJitsi() {
        if (this.jitsi === undefined) {
            return;
        }

        const { useHighQualityAudio = false } = this.props;

        this.jitsi.setLogLevel(Configuration.DEBUG ? this.jitsi.logLevels.DEBUG : this.jitsi.logLevels.ERROR);
        this.jitsi.init({
            desktopSharingChromeDisabled: true,
            desktopSharingFirefoxDisabled: true,
            disableThirdPartyRequests: true,
            // disable all audio processing besides bitrate compression
            disableAP: useHighQualityAudio,
            // not important to us
            disableAudioLevels: true,
            // webRTC is mono by default... note that switching to stereo takes up more bandwidth
            stereo: useHighQualityAudio,
        });

        this.manager.hasJitsiInitialized = true;
        this.connect();
    }

    public componentDidMount() {
        window.addEventListener("beforeunload", this.disconnect);
        window.addEventListener("unload", this.disconnect);
    }

    public componentWillUnmount() {
        window.removeEventListener("beforeunload", this.disconnect);
        window.removeEventListener("unload", this.disconnect);
    }

    public render() {
        const { isHost, children, showWelcomeDialog } = this.props;
        const { room, roomName, isReconnecting } = this.manager;
        return this.jitsi !== undefined && roomName !== undefined && room !== undefined ? (
            <>
                {children({
                    jitsi: this.jitsi,
                    room,
                    roomName,
                    isHost,
                    isReconnecting,
                    onLeaveRoom: this.handleLeaveRoom,
                    reconnect: this.reconnect,
                })}
                {this.jitsiUtil && showWelcomeDialog && <WelcomeDialog util={this.jitsiUtil} />}
            </>
        ) : null;
    }

    @action
    private reconnect = () => {
        this.manager.isReconnecting = true;
        this.connect();
    };

    private handleLeaveRoom = async () => {
        await this.manager.room?.leave();
        this.disconnect();
    };

    @action
    private connect = () => {
        if (this.jitsi === undefined) {
            return;
        }

        const { roomName } = this.props;

        try {
            // https://github.com/jitsi/lib-jitsi-meet/blob/master/doc/API.md#jitsiconnection
            const serviceUrlQuery = roomName === undefined ? "" : `?room=${roomName}`;
            const connection = new this.jitsi.JitsiConnection(null, null, {
                // HACKHACK: only works if you use Chrome with web security disabled.
                // Longer term this needs to point to our own server with the proper access control
                // headers in Nginx
                serviceUrl: `wss://meet.jit.si/xmpp-websocket${serviceUrlQuery}`,
                hosts: {
                    domain: "meet.jit.si",
                    muc: "conference.meet.jit.si",
                },
                clientNode: "http://jitsi.org/jitsimeet",
                enableLipSync: false,
            });
            connection.addEventListener(
                this.jitsi.events.connection.CONNECTION_ESTABLISHED,
                this.handleConnectionSuccess
            );
            connection.addEventListener(this.jitsi.events.connection.CONNECTION_FAILED, this.handleConnectionFailed);
            connection.addEventListener(this.jitsi.events.connection.CONNECTION_DISCONNECTED, this.handleDisconnect);

            this.manager.connection = connection;
            this.manager.roomName = roomName;
            connection.connect();
        } catch (e) {
            console.error(e);
        }
    };

    @action
    private handleConnectionSuccess = async () => {
        if (this.manager.room !== undefined) {
            console.info("Reconnected to room:", this.manager.room);
        }

        this.manager.isReconnecting = false;
        this.manager.room = this.manager.connection.initJitsiConference(this.manager.roomName, {
            openBridgeChannel: true,
        });
    };

    @action
    private handleConnectionFailed = (...args: any[]) => {
        console.warn("Connection failed", args);
    };

    @action
    private handleDisconnect = () => {
        console.info("Disconnected!");
    };

    @action
    private disconnect = () => {
        const { connection } = this.manager;
        connection?.removeEventListener(
            this.jitsi!.events.connection.CONNECTION_ESTABLISHED,
            this.handleConnectionSuccess
        );
        connection?.removeEventListener(this.jitsi!.events.connection.CONNECTION_FAILED, this.handleConnectionFailed);
        connection?.removeEventListener(this.jitsi!.events.connection.CONNECTION_DISCONNECTED, this.handleDisconnect);
        connection?.disconnect();
    };
}
